import { Location } from "@angular/common";
import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { AnyAccountJwtVOSpecs } from "@common/domain";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { AccountAuthActions } from "@youm-front/account-auth/data-access";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import {
  Conf2AccountSignInRequestDTO,
  Errors,
  GuestAccountSignInRequestDTO,
  KeycloakAccountSignInRequestDTO
} from "@youm-front/account/domain";
import { ApiActions } from "@youm-front/api/data-access";
import { Conf2Actions } from "@youm-front/conf2/data-access";
import { IAMAccountActions } from "@youm-front/iam-account/data-access";
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  mergeMap,
  skip,
  switchMap,
  take,
  tap
} from "rxjs/operators";
import { AccountActions } from "../actions";
import { conf2SignInFailureReason } from "../conf2-sign-in-failure";
import { AccountFacade } from "../facades";
import { AccountSelectors } from "../selectors";
import { AccountService } from "../services";

@Injectable()
export class AccountEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly accountFacade: AccountFacade,
    private readonly accountJwtService: AccountJwtService,
    private readonly accountService: AccountService,
    private readonly location: Location,
    private readonly router: Router,
    private readonly store: Store
  ) {}

  tokenSignIn$ = createEffect(
    () =>
      this.store
        .pipe(select(AccountSelectors.token))
        .pipe(
          tap((_) => (_ ? this.accountJwtService.accountJwtSet(_, true) : null))
        ),
    { dispatch: false }
  );

  conf2AccountSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Conf2Actions.conf2AccountSignIn),
      // Don't use old token.
      // Request and use new one.
      tap(() => this.accountJwtService.accountJwtDelete("conf2-sign-in")),
      switchMap((_) =>
        this.accountService
          .conf2AccountSignIn(new Conf2AccountSignInRequestDTO({ jwt: _.jwt }))
          .pipe(
            // Use sessionStorage to make it accessible from the 3rd party iframe
            // Host solution may use totally different origin
            tap((_) =>
              this.accountJwtService.accountJwtSet(_.value.token, true)
            ),
            mergeMap((_) => [
              AccountActions.signInSuccess({ token: _.value.token })
            ]),
            // ApiService rethrows the response body, which is null when an
            // error response has no body.
            catchError((_: Error) => [
              AccountActions.signInFailure({
                error: _?.message ?? ""
              }),
              AccountActions.conf2SignInFailure({
                reason: conf2SignInFailureReason(_)
              })
            ])
          )
      )
    )
  );

  guestSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.guestAccountSignIn),
      switchMap((_) =>
        this.accountService
          .guestAccountSignIn(
            new GuestAccountSignInRequestDTO({ identity: _.identity })
          )
          .pipe(
            tap((_) =>
              this.accountJwtService.accountJwtSet(_.value.token, true)
            ),
            mergeMap((_) => [
              AccountActions.guestAccountSignInSuccess({
                token: _.value.token
              })
            ]),
            catchError((_: Error) => [
              AccountActions.guestAccountSignInFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  keycloakSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountAuthActions.keycloakSignInSuccess),
      switchMap((_) =>
        this.accountService
          .keycloakAccountSignIn(
            new KeycloakAccountSignInRequestDTO({ token: _.token })
          )
          .pipe(
            tap((_) => this.accountJwtService.accountJwtSet(_.value.token)),
            mergeMap((_) => [
              AccountActions.signInSuccess({ token: _.value.token })
            ]),
            catchError((_: Error) => [
              AccountActions.signInFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  removeAuthRedirectQueryParam$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((_) => _ instanceof NavigationEnd),
        take(1),
        concatLatestFrom(() => this.accountJwtService.identityAccountJwtVO$),
        filter(([, identityAccountJwt]) => !identityAccountJwt),
        tap(() => this.accountService.removeAuthRedirectQueryParam())
      ),
    { dispatch: false }
  );

  iamAccountSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IAMAccountActions.iamAccountSignInSuccess),
      tap((_) => this.accountJwtService.accountJwtSet(_.jwt)),
      mergeMap((_) => [
        AccountActions.signInSuccess({
          token: _.jwt
        })
      ]),
      catchError((_: Error) => [
        AccountActions.signInFailure({
          error: _.message
        })
      ])
    )
  );

  signOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.signOut),
        tap(() => this.accountJwtService.accountJwtDelete("sign-out"))
      ),
    { dispatch: false }
  );

  me$ = createEffect(() =>
    this.accountJwtService.identityAccountJwtVO$.pipe(
      distinctUntilChanged(AnyAccountJwtVOSpecs.cmp),
      mergeMap((_) =>
        _
          ? this.accountService.me().pipe(
              mergeMap((_) => [AccountActions.meSuccess({ account: _ })]),
              catchError((_) => [
                AccountActions.meFailure({
                  error: _.message
                })
              ])
            )
          : []
      )
    )
  );

  meSignOut$ = createEffect(() =>
    this.accountJwtService.accountJwt$.pipe(
      distinctUntilChanged(),
      mergeMap((_) => (_ ? [] : [AccountActions.meSignOut()]))
    )
  );

  onUnauthorizedAccountJwt$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiActions.apiError),
        filter((_) => _.error.code === Errors.Unauthorized),
        tap(() => {
          this.accountFacade.signOut();
        })
      ),
    { dispatch: false }
  );

  navigateSameUrlOnAccountChange$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((_) => _ instanceof NavigationEnd),
        switchMap(() =>
          this.accountJwtService.anyAccountJwtVO$.pipe(
            skip(1),
            distinctUntilChanged(AnyAccountJwtVOSpecs.cmp),
            // this.router.url may contain some data after authorization redirects
            // use location to get the actual url from the address bar
            concatMap(() => this.router.navigateByUrl(this.location.path(true)))
          )
        )
      ),
    { dispatch: false }
  );

  redirectToAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.redirectToAuth),
        tap(() => this.accountService.redirectToAuth())
      ),
    { dispatch: false }
  );

  redirectAfterAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.signInSuccess),
        concatLatestFrom(() => this.accountJwtService.identityAccountJwtVO$),
        // Only identity account supports redirect after auth
        filter(([, identityAccountJwt]) => !!identityAccountJwt),
        mergeMap(() => this.accountService.redirectAfterAuth())
      ),
    {
      dispatch: false
    }
  );
}
